import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Globe, BookOpen, Loader2, FileText, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";
import axios from "axios";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { API } from "../config";

// Worker pdf.js servito localmente dal frontend (`/public/pdf.worker.min.js`)
// per rispettare la CSP "script-src 'self'" che blocca unpkg.com.
// Estensione `.js` invece di `.mjs` (Fabrizio 11/06/2026): il proxy
// statico di produzione serve i `.mjs` con MIME `application/octet-stream`,
// e Chrome rifiuta i moduli ES con MIME sbagliato. Mapping `.js → JS` è
// universale su tutti i CDN.
pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

const LANGUAGES = [
  { code: "EN", name: "English" }, { code: "IT", name: "Italiano" },
  { code: "FR", name: "Francais" }, { code: "ES", name: "Espanol" },
  { code: "DE", name: "Deutsch" }, { code: "PT", name: "Portugues" },
  { code: "NL", name: "Nederlands" }, { code: "PL", name: "Polski" },
  { code: "SE", name: "Svenska" }, { code: "NO", name: "Norsk" },
  { code: "FI", name: "Suomi" }, { code: "DK", name: "Dansk" },
  { code: "CZ", name: "Cesky" }, { code: "GR", name: "Ellinika" },
];

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
};

const PublicManualViewerPage = () => {
  const { manualId } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [manual, setManual] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [lang, setLang] = useState("EN");
  const [translating, setTranslating] = useState(false);
  const [viewMode, setViewMode] = useState("pdf");

  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(isMobile ? 0.55 : 1.2);
  const [pdfError, setPdfError] = useState(false);

  const pdfProxyUrl = `${API}/manuals/${manualId}/pdf-proxy`;

  useEffect(() => {
    setScale(isMobile ? 0.55 : 1.2);
  }, [isMobile]);

  useEffect(() => {
    axios.get(`${API}/manuals/public/${manualId}`)
      .then(res => setManual(res.data))
      .catch(err => {
        if (err.response?.status === 403 || err.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [manualId]);

  const handleLangChange = async (newLang) => {
    setLang(newLang);
    if (newLang !== "EN") {
      setViewMode("text");
      setTranslating(true);
      try {
        const res = await axios.get(`${API}/manuals/${manualId}/translate/${newLang}`);
        setManual(prev => ({ ...prev, title: res.data.title, description: res.data.description, sections: res.data.sections }));
      } catch {}
      finally { setTranslating(false); }
    } else {
      const res = await axios.get(`${API}/manuals/public/${manualId}`);
      setManual(res.data);
    }
  };

  const onDocumentLoadSuccess = useCallback(({ numPages }) => { setNumPages(numPages); setPdfError(false); }, []);
  const onDocumentLoadError = useCallback(() => { setPdfError(true); }, []);

  if (loading) return <div className="min-h-screen bg-lojack-black flex items-center justify-center"><div className="spinner" /></div>;

  if (notFound) return (
    <div className="min-h-screen bg-lojack-black flex items-center justify-center px-4">
      <div className="text-center">
        <BookOpen size={48} className="text-gray-700 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-white mb-2">Manuale non disponibile</h2>
        <p className="text-gray-500 mb-6">Questo manuale non esiste o non e accessibile pubblicamente.</p>
        <button onClick={() => navigate("/manuali")} className="btn-lojack">Torna alla Libreria</button>
      </div>
    </div>
  );

  if (!manual) return null;
  const sections = manual.sections || [];
  const hasPdf = !!manual.pdf_url;

  return (
    <div className="min-h-screen bg-lojack-black" data-testid="public-manual-viewer">
      {/* Header - Responsive */}
      <div className="sticky top-0 z-50 bg-[#0D0D0D]/95 backdrop-blur-sm border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2 sm:py-3">
          {/* Row 1: Back + Title */}
          <div className="flex items-center gap-2 mb-2 sm:mb-0">
            <button onClick={() => navigate("/manuali")} className="p-1.5 sm:p-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors flex-shrink-0" data-testid="public-manual-back">
              <ArrowLeft size={16} className="text-gray-400" />
            </button>
            <h1 className="text-sm sm:text-lg font-bold text-white truncate flex-1" data-testid="public-manual-title">{manual.title}</h1>
            {translating && <Loader2 className="w-4 h-4 text-lojack-red animate-spin flex-shrink-0" />}
          </div>
          {/* Row 2: Controls - scrollable on mobile */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0 scrollbar-hide">
            {hasPdf && (
              <div className="flex bg-[#1A1A1A] border border-gray-700 rounded-lg overflow-hidden flex-shrink-0">
                <button onClick={() => { setViewMode("pdf"); setLang("EN"); handleLangChange("EN"); }} className={`flex items-center gap-1 px-2.5 py-1 text-xs sm:text-sm transition-colors ${viewMode === "pdf" ? "bg-lojack-red text-white" : "text-gray-400"}`} data-testid="public-view-pdf">
                  <BookOpen size={12} /> PDF
                </button>
                <button onClick={() => setViewMode("text")} className={`flex items-center gap-1 px-2.5 py-1 text-xs sm:text-sm transition-colors ${viewMode === "text" ? "bg-lojack-red text-white" : "text-gray-400"}`} data-testid="public-view-text">
                  <FileText size={12} /> Testo
                </button>
              </div>
            )}
            {viewMode === "text" && (
              <div className="flex items-center gap-1 px-2 py-1 bg-[#1A1A1A] border border-gray-700 rounded-lg flex-shrink-0">
                <Globe size={12} className="text-lojack-red" />
                <select value={lang} onChange={e => handleLangChange(e.target.value)} disabled={translating} className="bg-transparent text-white text-xs sm:text-sm outline-none cursor-pointer" data-testid="public-lang-selector">
                  {LANGUAGES.map(l => <option key={l.code} value={l.code} style={{ backgroundColor: "#1A1A1A", color: "#fff" }}>{l.name}</option>)}
                </select>
              </div>
            )}
            {manual.pdf_url && (
              <a href={manual.pdf_url.startsWith("/api") ? `${API.replace("/api", "")}${manual.pdf_url}` : manual.pdf_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-2.5 py-1 bg-lojack-red/10 border border-lojack-red/30 rounded-lg text-lojack-red text-xs sm:text-sm font-medium flex-shrink-0" data-testid="public-manual-download">
                <Download size={12} /> Scarica
              </a>
            )}
          </div>
        </div>
      </div>

      {/* PDF View - Responsive */}
      {viewMode === "pdf" && hasPdf && (
        <div className="max-w-7xl mx-auto px-2 sm:px-6 py-3 sm:py-6" data-testid="public-pdf-viewer">
          {/* Controls - compact on mobile */}
          <div className="flex items-center justify-center gap-2 sm:gap-4 mb-3 bg-[#111] border border-gray-800 rounded-xl p-2 sm:p-3">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1} className="p-1.5 sm:p-2 rounded-lg bg-gray-800 hover:bg-gray-700 disabled:opacity-30 transition-colors">
              <ChevronLeft size={16} className="text-white" />
            </button>
            <span className="text-gray-300 text-xs sm:text-sm font-mono min-w-[70px] sm:min-w-[100px] text-center">
              {numPages ? `${currentPage} / ${numPages}` : "..."}
            </span>
            <button onClick={() => setCurrentPage(p => Math.min(numPages || 1, p + 1))} disabled={currentPage >= (numPages || 1)} className="p-1.5 sm:p-2 rounded-lg bg-gray-800 hover:bg-gray-700 disabled:opacity-30 transition-colors">
              <ChevronRight size={16} className="text-white" />
            </button>
            <div className="w-px h-5 bg-gray-700 hidden sm:block" />
            <button onClick={() => setScale(s => Math.max(0.3, s - 0.15))} className="p-1.5 sm:p-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors">
              <ZoomOut size={16} className="text-white" />
            </button>
            <span className="text-gray-400 text-[10px] sm:text-xs font-mono w-10 text-center">{Math.round(scale * 100)}%</span>
            <button onClick={() => setScale(s => Math.min(3, s + 0.15))} className="p-1.5 sm:p-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors">
              <ZoomIn size={16} className="text-white" />
            </button>
          </div>

          {/* PDF Document */}
          <div className="flex justify-center overflow-auto bg-[#1A1A1A] border border-gray-800 rounded-xl sm:rounded-2xl p-2 sm:p-4 min-h-[400px] sm:min-h-[600px]">
            <div className="max-w-full overflow-x-auto">
            {pdfError ? (
              <div className="flex flex-col items-center justify-center py-12 gap-4">
                <BookOpen size={40} className="text-gray-600" />
                <p className="text-gray-400 text-sm">Impossibile caricare il PDF.</p>
                {manual.pdf_url && (
                  <a href={manual.pdf_url.startsWith("/api") ? `${API.replace("/api", "")}${manual.pdf_url}` : manual.pdf_url} target="_blank" rel="noopener noreferrer" className="btn-lojack text-sm flex items-center gap-2">
                    <Download size={14} /> Apri PDF
                  </a>
                )}
              </div>
            ) : (
              <Document file={pdfProxyUrl} onLoadSuccess={onDocumentLoadSuccess} onLoadError={onDocumentLoadError} loading={<div className="flex flex-col items-center justify-center py-16 gap-3"><Loader2 className="w-7 h-7 text-lojack-red animate-spin" /><p className="text-gray-400 text-xs sm:text-sm">Caricamento PDF...</p></div>}>
                <Page pageNumber={currentPage} {...(isMobile ? { width: Math.min(window.innerWidth - 32, 500) } : { scale })} renderTextLayer={!isMobile} renderAnnotationLayer={!isMobile} loading={<div className="flex items-center justify-center py-16"><Loader2 className="w-5 h-5 text-lojack-red animate-spin" /></div>} />
              </Document>
            )}
            </div>
          </div>

          {/* Page nav - wrap on mobile */}
          {numPages && numPages > 1 && (
            <div className="flex items-center justify-center gap-1 mt-3 flex-wrap px-2">
              {Array.from({ length: numPages }, (_, i) => i + 1).map(page => (
                <button key={page} onClick={() => setCurrentPage(page)} className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg text-[10px] sm:text-xs font-mono transition-colors ${page === currentPage ? "bg-lojack-red text-white" : "bg-gray-800 text-gray-400 hover:bg-gray-700"}`}>{page}</button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Text View - Already responsive */}
      {viewMode === "text" && (
        <div className="max-w-4xl mx-auto px-3 sm:px-6 py-6 sm:py-8">
          <div className="bg-[#111] border border-gray-800 rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-6">
            <p className="text-gray-400 text-xs sm:text-sm">{manual.description}</p>
            {translating && <div className="mt-3 flex items-center gap-2 text-lojack-red text-xs sm:text-sm"><Loader2 className="w-4 h-4 animate-spin" />Traduzione AI in corso...</div>}
          </div>
          <div className="space-y-4 sm:space-y-6">
            {sections.map((section, i) => (
              <section key={section.id} className="bg-[#111] border border-gray-800 rounded-xl sm:rounded-2xl overflow-hidden">
                <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-800 bg-[#0D0D0D]">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <span className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-lojack-red/10 border border-lojack-red/30 flex items-center justify-center text-lojack-red text-xs sm:text-sm font-bold flex-shrink-0">{i + 1}</span>
                    <h2 className="text-sm sm:text-lg font-bold text-white">{section.title}</h2>
                  </div>
                </div>
                <div className="px-4 sm:px-6 py-4 sm:py-5">
                  <div className="text-gray-300 text-xs sm:text-sm leading-relaxed whitespace-pre-wrap">{section.content}</div>
                </div>
              </section>
            ))}
          </div>
          <div className="mt-8 pt-4 border-t border-gray-800 text-center">
            <p className="text-gray-600 text-[10px] sm:text-xs">&copy; {new Date().getFullYear()} LoJack Italia S.r.l. / CalAmp Corp.</p>
            {lang !== "EN" && <p className="text-gray-700 text-[10px] sm:text-xs mt-1">Traduzione automatica AI</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicManualViewerPage;
